import { Flex, Text } from "@chakra-ui/react";
import { GroupBase, OptionProps, Props, Select } from "chakra-react-select";
import { useEffect, useState } from "react";
import { EntityForTheCompanyModel, useEntitiesForTheCompany } from "hooks/useEntitiesForTheCompany";

const CustomOption = ({ data, innerProps }: OptionProps<EntityForTheCompanyModel>) => (
  <Flex
    {...innerProps}
    p={2}
    gap={4}
    borderRadius="md"
    _hover={{ bg: "gray.50" }}
    cursor="pointer"
    alignItems="center"
  >
    <Flex direction="column" alignItems="start">
      <Text color="gray.600">{data.name}</Text>
      <Text fontSize="sm" color="gray.500">
        {data.address}
      </Text>
    </Flex>
  </Flex>
);

interface EntitySelectProps
  extends Props<EntityForTheCompanyModel, false, GroupBase<EntityForTheCompanyModel>> {
  companyId?: string;
  entityId: string | undefined;
  setEntityId: (entityId: string | undefined) => void;
}

export const EntitySelect = ({ companyId, entityId, setEntityId, ...props }: EntitySelectProps) => {
  const { data: entities, isLoading } = useEntitiesForTheCompany(companyId);

  const [options, setOptions] = useState<EntityForTheCompanyModel[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<EntityForTheCompanyModel | null>(null);

  useEffect(() => {
    setOptions(entities?.sort((a, b) => a.name.localeCompare(b.name)) ?? []);
  }, [entities]);

  useEffect(() => {
    if (entityId && entities) {
      const newSelectedEntity = entities.find(e => e.id === entityId) ?? null;
      setSelectedEntity(newSelectedEntity);
    } else if (!entityId) {
      setSelectedEntity(null);
    }
  }, [entityId, entities]);

  const handleEntityChange = (entity: EntityForTheCompanyModel | null) => {
    setSelectedEntity(entity);
    setEntityId(entity?.id ?? undefined);
  };

  return (
    <Select<EntityForTheCompanyModel>
      options={options}
      getOptionLabel={e => e.name}
      getOptionValue={e => e.id}
      components={{ Option: CustomOption }}
      isLoading={isLoading}
      chakraStyles={{
        placeholder: provided => ({
          ...provided,
          textAlign: "left",
        }),
        singleValue: provided => ({
          ...provided,
          textAlign: "left",
        }),
      }}
      placeholder="Select an entity..."
      noOptionsMessage={() => "No entities found."}
      onChange={handleEntityChange}
      value={selectedEntity}
      variant="primary"
      filterOption={(option, inputValue) =>
        [option.data.name, option.data.address].some(field =>
          field?.toLowerCase().includes(inputValue.toLowerCase())
        )
      }
      isClearable={true}
      {...props}
    />
  );
};
